import { createBasicHeuristicBot, type BasicHeuristicBot } from "@fate-kingdom/bots";
import {
  createPlayerObservation,
  PUBLIC_BETA_RULES_VERSION,
  getStateHash,
  replayActions,
  type EngineContext,
  type GameAction,
  type GameEvent,
  type CurrentCoreAlphaGameState,
  type PlayerId,
  type PlayerDecisionContext,
  type PlayerObservationV1,
  type ReplayDefinitionV3,
  type ReplayDefinitionV4,
  type ReplayDefinitionV5,
  type ReplayV3Result,
  type ReplayV4Result,
  type ReplayV5Result,
} from "@fate-kingdom/engine";
import {
  createHeadlessMatchRunner,
  createSafePlayerDecisionContext,
  isActionInLegalSet,
  type HeadlessMatchRunner,
  type HeadlessRunnerCreationResult,
} from "@fate-kingdom/headless-runner";
import {
  PUBLIC_BETA_TEST_DECK_CARD_IDS,
  PUBLIC_BETA_TEST_DECKS,
  createPublicBetaMatchSetup,
} from "@fate-kingdom/testkit";

import {
  alphaPresentationKind,
  type AlphaPresentationTransaction,
} from "./presentation/presentation-transaction";

export const ALPHA_DEFAULT_SEED = "fate-kingdom-alpha-001";
export const ALPHA_MAXIMUM_ACTIONS = 1_000;
export const ALPHA_MAXIMUM_TURNS = 100;
export const ALPHA_MAXIMUM_AI_ACTIONS_PER_TURN = 64;
export const ALPHA_MAXIMUM_SEED_CODE_POINTS = 96;
export const ALPHA_MAXIMUM_PUBLIC_SUMMARIES = 12;

export type AlphaDeckId = "assault" | "balanced";

export interface AlphaMatchConfig {
  readonly humanPlayerId: PlayerId;
  readonly humanDeckId: AlphaDeckId;
  readonly aiDeckId: AlphaDeckId;
  readonly seed: string;
}

export interface AlphaAiSummary {
  readonly actionNumber: number;
  readonly turnNumber: number;
  readonly phase: string;
  readonly summary: string;
}

export interface AlphaSessionFailure {
  readonly code: string;
  readonly message: string;
  readonly actionCount: number;
  readonly turnNumber: number;
}

export interface AlphaReplayVerification {
  readonly ok: boolean;
  readonly replayVersion: number;
  readonly actionCount: number;
  readonly replayActionCount: number;
  readonly actionCountMatches: boolean;
  readonly currentStateHash: string;
  readonly reconstructedStateHash: string | null;
  readonly stateHashMatches: boolean;
  readonly statusMatches: boolean;
  readonly winnerMatches: boolean;
  readonly turnNumberMatches: boolean;
  readonly activePlayerMatches: boolean;
  readonly phaseMatches: boolean;
  readonly message: string;
}

export type AlphaSeedValidationResult =
  { readonly ok: true; readonly seed: string } | { readonly ok: false; readonly message: string };

export interface AlphaSessionDependencies {
  readonly createRunner?: (
    setup: ReturnType<typeof createAlphaMatchSetup>,
  ) => HeadlessRunnerCreationResult;
  readonly createBot?: (playerId: PlayerId) => BasicHeuristicBot;
  readonly createDecisionContext?: (
    state: Readonly<CurrentCoreAlphaGameState>,
    playerId: PlayerId,
    step: number,
  ) => PlayerDecisionContext;
  readonly replay?: (
    replay: ReplayDefinitionV3 | ReplayDefinitionV4 | ReplayDefinitionV5,
    context: EngineContext,
  ) => ReplayV3Result | ReplayV4Result | ReplayV5Result;
  readonly stateHash?: (state: Readonly<CurrentCoreAlphaGameState>) => string;
}

export interface AlphaSessionSnapshot {
  readonly config: AlphaMatchConfig;
  readonly observation: PlayerObservationV1;
  /** Only exposed while it is the human player's turn. */
  readonly humanLegalActions: readonly GameAction[];
  readonly aiSummaries: readonly AlphaAiSummary[];
  readonly actionCount: number;
  readonly aiActionsThisTurn: number;
  readonly failure: AlphaSessionFailure | null;
}

export type AlphaActionResult =
  | {
      readonly ok: true;
      readonly events: readonly GameEvent[];
      readonly transaction: AlphaPresentationTransaction;
    }
  | { readonly ok: false; readonly failure: AlphaSessionFailure };

export type AlphaSessionCreationResult =
  | { readonly ok: true; readonly controller: AlphaSessionController }
  | { readonly ok: false; readonly failure: AlphaSessionFailure };

function cloneSerializable<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function getOpponentPlayerId(playerId: PlayerId): PlayerId {
  return playerId === "player-1" ? "player-2" : "player-1";
}

export function validateAlphaSeed(seed: string): AlphaSeedValidationResult {
  const normalized = seed.trim();
  if (normalized.length === 0) {
    return { ok: false, message: "Enter a match seed before starting." };
  }
  const containsControlCharacter = Array.from(normalized).some((character) => {
    const codePoint = character.codePointAt(0)!;
    return codePoint <= 0x1f || (codePoint >= 0x7f && codePoint <= 0x9f);
  });
  if (containsControlCharacter) {
    return { ok: false, message: "Match seeds cannot contain control characters." };
  }
  if (Array.from(normalized).length > ALPHA_MAXIMUM_SEED_CODE_POINTS) {
    return {
      ok: false,
      message: `Match seeds may contain at most ${ALPHA_MAXIMUM_SEED_CODE_POINTS} characters.`,
    };
  }
  return { ok: true, seed: normalized };
}

export function alphaExportFilename(
  kind: "replay" | "failure",
  seed: string,
  actionCount: number,
): string {
  const sanitized = seed
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, "-")
    .replace(/^-+|-+$/gu, "")
    .slice(0, 48)
    .replace(/-+$/gu, "");
  return `fate-kingdom-alpha-${sanitized || "seed"}-${String(actionCount)}.${kind === "replay" ? "replay" : "failure"}.json`;
}

function alphaMatchId(config: AlphaMatchConfig): string {
  return `alpha-${config.humanPlayerId}-${config.seed}`;
}

function createAlphaMatchSetup(config: AlphaMatchConfig) {
  const setup = createPublicBetaMatchSetup(config.seed, alphaMatchId(config));
  const playerOneDeckId =
    config.humanPlayerId === "player-1" ? config.humanDeckId : config.aiDeckId;
  const playerTwoDeckId =
    config.humanPlayerId === "player-2" ? config.humanDeckId : config.aiDeckId;

  return {
    ...setup,
    rulesVersion: PUBLIC_BETA_RULES_VERSION,
    decks: {
      "player-1": {
        deckId: PUBLIC_BETA_TEST_DECKS[playerOneDeckId].id,
        cardDefinitionIds: PUBLIC_BETA_TEST_DECK_CARD_IDS[playerOneDeckId],
      },
      "player-2": {
        deckId: PUBLIC_BETA_TEST_DECKS[playerTwoDeckId].id,
        cardDefinitionIds: PUBLIC_BETA_TEST_DECK_CARD_IDS[playerTwoDeckId],
      },
    },
  };
}

export function publicActionSummary(action: GameAction): string {
  switch (action.type) {
    case "found-land":
      return "founded a Land";
    case "develop-land":
      return "developed a Land";
    case "deploy-commander":
      return "deployed a Commander";
    case "replace-commander":
      return "replaced a Commander";
    case "deploy-army":
      return "stationed an Army";
    case "disband-army":
      return "disbanded an Army";
    case "disband-commander":
      return "disbanded a Commander";
    case "discard-army-for-upkeep":
      return "resolved Supply";
    case "attack":
      return action.targetLaneId === undefined || action.targetLaneId === action.laneId
        ? "launched a Direct Attack"
        : "launched a Flank Attack";
    case "advance-phase":
      return "advanced the phase";
    case "play-event":
      return "resolved an Event";
    case "end-turn":
      return "ended the turn";
    default:
      return "took an action";
  }
}

function failure(
  code: string,
  message: string,
  actionCount: number,
  turnNumber: number,
): AlphaSessionFailure {
  return { code, message, actionCount, turnNumber };
}

/**
 * Browser scheduling deliberately lives outside this DOM-free controller.
 * Each caller invocation applies at most one authoritative action.
 */
export class AlphaSessionController {
  readonly #config: AlphaMatchConfig;
  readonly #runner: HeadlessMatchRunner;
  readonly #humanPlayerId: PlayerId;
  readonly #aiPlayerId: PlayerId;
  readonly #bot: BasicHeuristicBot;
  readonly #createDecisionContext: NonNullable<AlphaSessionDependencies["createDecisionContext"]>;
  readonly #replay: NonNullable<AlphaSessionDependencies["replay"]>;
  readonly #stateHash: NonNullable<AlphaSessionDependencies["stateHash"]>;
  readonly #aiSummaries: AlphaAiSummary[] = [];
  #failure: AlphaSessionFailure | null = null;
  #aiTurnNumber: number | null = null;
  #aiActionsThisTurn = 0;
  #lastAiObservationHash: string | null = null;
  #lastAiReasonCodes: string[] = [];

  constructor(
    config: AlphaMatchConfig,
    runner: HeadlessMatchRunner,
    dependencies: AlphaSessionDependencies = {},
  ) {
    this.#config = cloneSerializable(config);
    this.#runner = runner;
    this.#humanPlayerId = config.humanPlayerId;
    this.#aiPlayerId = getOpponentPlayerId(config.humanPlayerId);
    this.#bot =
      dependencies.createBot?.(this.#aiPlayerId) ??
      createBasicHeuristicBot({ playerId: this.#aiPlayerId });
    this.#createDecisionContext =
      dependencies.createDecisionContext ?? createSafePlayerDecisionContext;
    this.#replay =
      dependencies.replay ??
      ((replay, context) => {
        switch (replay.replayVersion) {
          case 3:
            return replayActions(replay, context);
          case 4:
            return replayActions(replay, context);
          case 5:
            return replayActions(replay, context);
        }
      });
    this.#stateHash = dependencies.stateHash ?? getStateHash;
  }

  get snapshot(): AlphaSessionSnapshot {
    const state = this.#runner.state;
    const observation = createPlayerObservation(state, this.#humanPlayerId);
    const humanLegalActions =
      this.#failure === null &&
      state.status.type === "active" &&
      state.activePlayerId === this.#humanPlayerId
        ? this.#runner.getLegalActions()
        : [];

    return cloneSerializable({
      config: this.#config,
      observation,
      humanLegalActions,
      aiSummaries: this.#aiSummaries,
      actionCount: this.#runner.trace.length,
      aiActionsThisTurn: this.#aiActionsThisTurn,
      failure: this.#failure,
    });
  }

  get isAiTurn(): boolean {
    const state = this.#runner.state;
    return (
      this.#failure === null &&
      state.status.type === "active" &&
      state.activePlayerId === this.#aiPlayerId
    );
  }

  get isFinished(): boolean {
    return this.#runner.isFinished();
  }

  applyHumanAction(action: GameAction): AlphaActionResult {
    const state = this.#runner.state;
    if (this.#failure !== null) return { ok: false, failure: this.#failure };
    if (state.status.type === "finished") {
      return this.#reject("MATCH_FINISHED", "This match has already finished.");
    }
    if (state.activePlayerId !== this.#humanPlayerId) {
      return this.#reject("NOT_HUMAN_TURN", "Wait for the AI to finish its current action.");
    }
    const boundsFailure = this.#checkBounds();
    if (boundsFailure !== null) return { ok: false, failure: boundsFailure };

    const legalActions = this.#runner.getLegalActions();
    if (!isActionInLegalSet(action, legalActions)) {
      return this.#reject(
        "ACTION_NOT_IN_LEGAL_SET",
        "That choice is no longer legal in the current deterministic match state.",
      );
    }
    return this.#apply(action);
  }

  stepAi(): AlphaActionResult {
    const state = this.#runner.state;
    if (this.#failure !== null) return { ok: false, failure: this.#failure };
    if (state.status.type === "finished") {
      return this.#reject("MATCH_FINISHED", "This match has already finished.");
    }
    if (state.activePlayerId !== this.#aiPlayerId) {
      return this.#reject("NOT_AI_TURN", "The AI may only act during its own turn.");
    }
    const boundsFailure = this.#checkBounds();
    if (boundsFailure !== null) return { ok: false, failure: boundsFailure };
    if (this.#aiTurnNumber !== state.turnNumber) {
      this.#aiTurnNumber = state.turnNumber;
      this.#aiActionsThisTurn = 0;
    }
    if (this.#aiActionsThisTurn >= ALPHA_MAXIMUM_AI_ACTIONS_PER_TURN) {
      return this.#stop(
        "AI_ACTION_TURN_LIMIT",
        `The AI reached its ${ALPHA_MAXIMUM_AI_ACTIONS_PER_TURN}-action turn safety limit.`,
      );
    }

    let action: GameAction | null;
    try {
      const context = this.#createDecisionContext(
        state,
        this.#aiPlayerId,
        this.#runner.trace.length,
      );
      this.#lastAiObservationHash = context.observationHash;
      this.#lastAiReasonCodes = [];
      action = this.#bot.nextAction(context);
      const decision = this.#bot.decisions.at(-1);
      if (decision !== undefined) {
        this.#lastAiObservationHash = decision.observationHash;
        this.#lastAiReasonCodes = decision.selectedReasons.map((reason) => reason.code);
      }
    } catch (error) {
      return this.#stop(
        "AI_DECISION_FAILURE",
        error instanceof Error ? error.message : "The AI could not choose a legal action.",
      );
    }
    if (action === null) {
      return this.#stop("AI_ACTION_SOURCE_ENDED", "The AI ended without supplying an action.");
    }

    const legalActions = this.#runner.getLegalActions();
    if (!isActionInLegalSet(action, legalActions)) {
      return this.#stop(
        "AI_ACTION_NOT_IN_LEGAL_SET",
        "The AI returned an action outside its authoritative legal-action set.",
      );
    }

    this.#aiActionsThisTurn += 1;
    const result = this.#apply(action);
    if (result.ok) {
      this.#aiSummaries.push({
        actionNumber: this.#runner.trace.length,
        turnNumber: state.turnNumber,
        phase: state.phase,
        summary: `AI ${publicActionSummary(action)}.`,
      });
      if (this.#aiSummaries.length > ALPHA_MAXIMUM_PUBLIC_SUMMARIES) this.#aiSummaries.shift();
    }
    return result;
  }

  exportReplay(): ReplayDefinitionV3 | ReplayDefinitionV4 | ReplayDefinitionV5 {
    return this.#runner.exportReplay();
  }

  verifyReplay(): AlphaReplayVerification {
    const replay = this.exportReplay();
    const currentState = this.#runner.state;
    const currentStateHash = this.#stateHash(currentState);
    const result = this.#replay(replay, {
      rulesVersion: this.#runner.state.rulesVersion,
      cardDataVersion: this.#runner.state.cardDataVersion,
    });
    if (
      !result.ok ||
      result.state === null ||
      (result.state.schemaVersion !== 3 &&
        result.state.schemaVersion !== 4 &&
        result.state.schemaVersion !== 5)
    ) {
      return {
        ok: false,
        replayVersion: replay.replayVersion,
        actionCount: this.#runner.trace.length,
        replayActionCount: replay.actions.length,
        actionCountMatches: replay.actions.length === this.#runner.trace.length,
        currentStateHash,
        reconstructedStateHash: null,
        stateHashMatches: false,
        statusMatches: false,
        winnerMatches: false,
        turnNumberMatches: false,
        activePlayerMatches: false,
        phaseMatches: false,
        message: result.ok ? "Replay returned an unexpected state schema." : result.failure.message,
      };
    }
    const reconstructedState = result.state;
    const reconstructedStateHash = this.#stateHash(reconstructedState);
    const actionCountMatches = replay.actions.length === this.#runner.trace.length;
    const stateHashMatches = reconstructedStateHash === currentStateHash;
    const statusMatches = reconstructedState.status.type === currentState.status.type;
    const winnerMatches =
      reconstructedState.status.type === "finished" && currentState.status.type === "finished"
        ? reconstructedState.status.winnerPlayerId === currentState.status.winnerPlayerId &&
          reconstructedState.status.reason === currentState.status.reason
        : reconstructedState.status.type === "active" && currentState.status.type === "active";
    const turnNumberMatches = reconstructedState.turnNumber === currentState.turnNumber;
    const activePlayerMatches = reconstructedState.activePlayerId === currentState.activePlayerId;
    const phaseMatches = reconstructedState.phase === currentState.phase;
    const checks = {
      actionCountMatches,
      stateHashMatches,
      statusMatches,
      winnerMatches,
      turnNumberMatches,
      activePlayerMatches,
      phaseMatches,
    };
    const mismatches = Object.entries(checks)
      .filter(([, matches]) => !matches)
      .map(([name]) => name.replace(/Matches$/u, ""));
    const ok = mismatches.length === 0;
    return {
      ok,
      replayVersion: replay.replayVersion,
      actionCount: this.#runner.trace.length,
      replayActionCount: replay.actions.length,
      ...checks,
      currentStateHash,
      reconstructedStateHash,
      message: ok
        ? `Replay V${String(replay.replayVersion)} verified against the current match state.`
        : `Replay reconstruction differs from the current match: ${mismatches.join(", ")}.`,
    };
  }

  exportFailureReport(): Readonly<Record<string, unknown>> {
    const state = this.#runner.state;
    const lastTraceEntry = this.#runner.trace.at(-1);
    return cloneSerializable({
      reportVersion: 1,
      kind: "fate-kingdom-alpha-pvai-failure",
      config: this.#config,
      failure: this.#failure,
      actionCount: this.#runner.trace.length,
      turnNumber: state.turnNumber,
      phase: state.phase,
      activePlayerId: state.activePlayerId,
      humanPlayerId: this.#humanPlayerId,
      aiPlayerId: this.#aiPlayerId,
      lastAction: lastTraceEntry?.action ?? null,
      recentPublicEvents: this.#aiSummaries,
      lastAiObservationHash: this.#lastAiObservationHash,
      lastAiReasonCodes: this.#lastAiReasonCodes,
      expectedCurrentStateHash: this.#stateHash(state),
      replayVerification: this.verifyReplay(),
      replay: this.exportReplay(),
    });
  }

  #checkBounds(): AlphaSessionFailure | null {
    const state = this.#runner.state;
    if (this.#runner.trace.length >= ALPHA_MAXIMUM_ACTIONS) {
      this.#stop(
        "MATCH_ACTION_LIMIT",
        `The match reached its ${ALPHA_MAXIMUM_ACTIONS}-action safety limit.`,
      );
      return this.#failure;
    }
    if (state.turnNumber > ALPHA_MAXIMUM_TURNS) {
      this.#stop(
        "MATCH_TURN_LIMIT",
        `The match exceeded its ${ALPHA_MAXIMUM_TURNS}-turn safety limit.`,
      );
      return this.#failure;
    }
    return null;
  }

  #apply(action: GameAction): AlphaActionResult {
    const beforeObservation = createPlayerObservation(this.#runner.state, this.#humanPlayerId);
    const actionSequence = this.#runner.trace.length + 1;
    const applied = this.#runner.apply(action);
    if (!applied.ok) {
      return this.#stop(applied.failure.code, applied.failure.message);
    }
    const events = cloneSerializable(applied.entry.events);
    return {
      ok: true,
      events,
      transaction: cloneSerializable({
        actionSequence,
        actorPlayerId: action.playerId,
        action,
        events,
        beforeObservation,
        afterObservation: createPlayerObservation(this.#runner.state, this.#humanPlayerId),
        presentationKind: alphaPresentationKind(action),
      }),
    };
  }

  #reject(code: string, message: string): AlphaActionResult {
    const state = this.#runner.state;
    return {
      ok: false,
      failure: failure(code, message, this.#runner.trace.length, state.turnNumber),
    };
  }

  #stop(code: string, message: string): AlphaActionResult {
    const state = this.#runner.state;
    this.#failure = failure(code, message, this.#runner.trace.length, state.turnNumber);
    return { ok: false, failure: this.#failure };
  }
}

export function createAlphaSession(
  config: AlphaMatchConfig,
  dependencies: AlphaSessionDependencies = {},
): AlphaSessionCreationResult {
  const seedResult = validateAlphaSeed(config.seed);
  if (!seedResult.ok) {
    return { ok: false, failure: failure("INVALID_SEED", seedResult.message, 0, 0) };
  }
  const normalizedConfig = { ...config, seed: seedResult.seed };
  const setup = createAlphaMatchSetup(normalizedConfig);
  const created =
    dependencies.createRunner?.(setup) ??
    createHeadlessMatchRunner(setup, {
      maximumActions: ALPHA_MAXIMUM_ACTIONS,
      maximumTurns: ALPHA_MAXIMUM_TURNS,
      invariantCheck: "every-step",
      traceMode: "full",
    });
  if (!created.ok) {
    return {
      ok: false,
      failure: failure(created.failure.code, created.failure.message, 0, 0),
    };
  }
  return {
    ok: true,
    controller: new AlphaSessionController(normalizedConfig, created.runner, dependencies),
  };
}
